import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, CalendarRange, LineChart, Command as CommandIcon, Users, X, ArrowRight, ArrowLeft,
} from "lucide-react";

const KEY = "meetiq_tour_done_v1";

const BASE = [
  {
    icon: Sparkles,
    title: "Welcome to MeetIQ360",
    body: "Your AI meeting intelligence workspace by Yanthra Labs. Record a meeting and we turn it into validated decisions, owned tasks and board-ready minutes.",
  },
  {
    icon: CalendarRange,
    title: "Start with a meeting",
    body: "Create a meeting with an agenda, record it in the browser, and watch live captions and a running transcript-quality meter as you talk.",
    cta: { to: "/meetings", label: "Go to Meetings" },
  },
  {
    icon: LineChart,
    title: "Measure what changed",
    body: "Insights shows hours saved, decision velocity, execution rate and who is carrying the load — per week, over time.",
    cta: { to: "/insights", label: "Open Insights" },
  },
  {
    icon: CommandIcon,
    title: "Press ⌘K for anything",
    body: "Jump to any meeting, task or decision, switch theme or language, or create a meeting — without leaving the page.",
  },
];

const ADMIN_STEP = {
  icon: Users,
  title: "You're an administrator",
  body: "Workspaces & users lets you add people, set roles, meeting quotas and account validity — and review every workspace activity.",
  cta: { to: "/admin/users", label: "Manage workspaces" },
};

export default function OnboardingTour({ isAdmin }) {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (localStorage.getItem(KEY)) return undefined;
    const t = setTimeout(() => setOpen(true), 1100);
    return () => clearTimeout(t);
  }, []);

  const steps = isAdmin ? [...BASE, ADMIN_STEP] : BASE;
  const step = steps[Math.min(i, steps.length - 1)];
  const last = i >= steps.length - 1;

  const dismiss = () => {
    localStorage.setItem(KEY, "1");
    setOpen(false);
  };

  if (!open) return null;
  const Icon = step.icon;

  return (
    <div
      data-testid="onboarding-tour"
      className="fixed z-[60] bottom-24 left-4 right-4 sm:left-6 sm:right-auto sm:w-[380px] yl-scale-in"
    >
      <div className="relative yl-glass yl-beam rounded-3xl shadow-lift p-6">
        <button
          type="button"
          data-testid="onboarding-close"
          onClick={dismiss}
          aria-label="Dismiss tour"
          className="absolute top-4 right-4 w-7 h-7 rounded-full border border-border grid place-items-center text-muted-foreground hover:text-primary transition-colors"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2.2} />
        </button>

        <div
          className="w-10 h-10 rounded-xl grid place-items-center"
          style={{ background: "hsl(var(--cyan) / .14)" }}
        >
          <Icon className="w-[18px] h-[18px]" style={{ color: "hsl(var(--ink-info))" }} strokeWidth={2} />
        </div>

        <h3 className="font-heading text-lg mt-4 pr-6">{step.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed mt-2.5">{step.body}</p>

        {step.cta && (
          <Link
            to={step.cta.to}
            data-testid="onboarding-step-cta"
            onClick={dismiss}
            className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:gap-2.5 transition-[gap]"
          >
            {step.cta.label}
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} />
          </Link>
        )}

        <div className="flex items-center justify-between gap-3 mt-6 pt-5 border-t border-border/70">
          <div className="flex items-center gap-1.5">
            {steps.map((s, idx) => (
              <span
                key={s.title}
                className="h-1.5 rounded-full transition-[width,background-color] duration-300"
                style={{
                  width: idx === i ? 18 : 6,
                  background: idx === i ? "hsl(var(--cyan))" : "hsl(var(--border))",
                }}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {i > 0 && (
              <button
                type="button"
                data-testid="onboarding-back"
                onClick={() => setI((v) => v - 1)}
                className="inline-flex items-center gap-1 h-9 px-3 rounded-full text-xs font-bold text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.2} />
                Back
              </button>
            )}
            <button
              type="button"
              data-testid={last ? "onboarding-finish" : "onboarding-next"}
              onClick={() => (last ? dismiss() : setI((v) => v + 1))}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-primary text-primary-foreground text-xs font-bold hover:bg-[hsl(var(--primary-hover))] transition-colors"
            >
              {last ? "Got it" : "Next"}
              {!last && <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
